/**
 * 全局样式配置 - 统一设计系统
 * 确保所有页面风格一致
 */

export const GlobalStyles = {
  colors: {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    secondary: '#3b82f6',
    accent: '#ec4899',
    gold: '#fbbf24',
    background: 'linear-gradient(135deg, #fafbff 0%, #fef5ff 50%, #fffafc 100%)',
    text: {
      primary: '#1f2937',
      secondary: '#4b5563',
      tertiary: '#9ca3af',
    },
  },
  
  card: {
    borderRadius: '16px',
    padding: '24px',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1), 0 8px 16px rgba(0,0,0,0.05)',
    border: '1px solid rgba(255,255,255,0.2)',
    hover: {
      transform: 'scale(1.02)',
      boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
    },
  },
  
  button: {
    height: '40px',
    borderRadius: '12px',
    padding: '0 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    hover: {
      transform: 'scale(1.05)',
      boxShadow: '0 8px 20px rgba(102, 126, 234, 0.6)',
    },
  },
  
  input: {
    height: '40px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    focusBorder: '2px solid #667eea',
  },
  
  tag: {
    borderRadius: '6px',
    padding: '4px 12px',
    fontSize: '12px',
  },
  
  typography: {
    h1: {
      fontSize: '32px',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body: {
      fontSize: '14px',
      lineHeight: 1.6,
      letterSpacing: '0.3px',
    },
    button: {
      fontSize: '14px',
      fontWeight: 600,
    },
    number: {
      fontFamily: "'Courier New', monospace",
    },
  },
  
  layout: {
    container: {
      maxWidth: '1400px',
      padding: '24px',
      margin: '0 auto',
    },
    spacing: {
      xs: '4px',
      sm: '8px',
      md: '12px',
      lg: '16px',
      xl: '24px',
      xxl: '32px',
    },
  },
  
  animation: {
    duration: '0.3s',
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    pageEnter: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
    },
  },
  
  responsive: {
    mobile: {
      maxWidth: '767px',
      containerPadding: '16px',
      cardPadding: '16px',
      fontSizeScale: 0.9,
    },
    tablet: {
      minWidth: '768px',
      maxWidth: '1023px',
      containerPadding: '20px',
      cardPadding: '20px',
    },
    desktop: {
      minWidth: '1024px',
    },
  },
}

// CSS 类名常量
export const CSS_CLASSES = {
  // 卡片样式
  card: 'bg-white/90 backdrop-blur-md rounded-2xl p-6 shadow-2xl border border-white/20 hover:scale-[1.02] hover:shadow-3xl transition-all duration-300',
  
  // 按钮样式
  button: 'h-10 px-6 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold shadow-lg hover:scale-105 hover:shadow-xl transition-all duration-300',
  
  // 输入框样式
  input: 'h-10 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300',
  
  // 标签样式
  tag: 'px-3 py-1 rounded-md text-xs font-medium',
  
  // 页面容器
  container: 'max-w-7xl mx-auto px-6',
  
  // 渐变背景
  background: 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500',
  
  // 文字颜色
  textPrimary: 'text-gray-900',
  textSecondary: 'text-gray-600',
  textTertiary: 'text-gray-400',
  
  // 动画
  fadeInUp: 'animate-fade-in-up',
  pulse: 'animate-pulse',
  bounce: 'animate-bounce',
}

// 响应式断点
export const BREAKPOINTS = {
  mobile: 'max-width: 767px',
  tablet: 'min-width: 768px and max-width: 1023px',
  desktop: 'min-width: 1024px',
}

// 导出常用的样式对象
export const COMMON_STYLES = {
  // 页面进入动画
  pageEnter: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
  },
  
  // 卡片hover动画
  cardHover: {
    whileHover: { 
      scale: 1.02,
      transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
    },
  },
  
  // 按钮hover动画
  buttonHover: {
    whileHover: { 
      scale: 1.05,
      transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
    },
  },
  
  // 加载动画
  loading: {
    animate: { 
      opacity: [0.5, 1, 0.5],
      transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
    },
  },
}
